import { directNestOrigin, normalizeNestRoutes, type NestRoute } from "../nest/routes"
import { isNativeMobilePlatform } from "../platform/detect"
import { secureComputerRegistry, type SecureComputerConnection, type SecureComputerDescriptor, type SecureRegistryState } from "../platform/secureRegistry"
import type { AddRemoteComputerInput, ComputerAccessSummary, ComputerBackend, ComputerConnection, ComputerListState, ComputerSummary } from "./types"

const BROWSER_REGISTRY_KEY = "wildlark:computers:v2"

interface BrowserComputer {
    addedAt: string
    name: string
    nodeId: string
    routes: NestRoute[]
    token: string
}

interface BrowserRegistryState {
    version: 2
    activeNodeId?: string
    computers: BrowserComputer[]
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null
}

function accessForRoutes(routes: readonly NestRoute[]): ComputerAccessSummary {
    const relay = routes.find((route) => route.type === "relay")
    if (relay) return { provider: "relay", status: "ready", url: relay.relayUrl }
    const direct = directNestOrigin(routes)
    if (!direct) return { provider: "private-network", status: "unavailable" }
    return {
        provider: "private-network",
        status: "ready",
        url: direct,
    }
}

function parseBrowserComputer(value: unknown): BrowserComputer | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null
    const record = value as Record<string, unknown>
    const addedAt = nonEmptyString(record.addedAt)
    const name = nonEmptyString(record.name)
    const nodeId = nonEmptyString(record.nodeId)
    const token = nonEmptyString(record.token)
    if (!addedAt || !name || !nodeId || !token) return null
    try {
        return { addedAt, name, nodeId, routes: normalizeNestRoutes(record.routes), token }
    } catch {
        return null
    }
}

function emptyBrowserRegistry(): BrowserRegistryState {
    return { version: 2, computers: [] }
}

function parseBrowserRegistry(value: unknown): BrowserRegistryState {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return emptyBrowserRegistry()
    const record = value as Record<string, unknown>
    if (record.version !== 2 || !Array.isArray(record.computers)) return emptyBrowserRegistry()
    const computers = record.computers.map(parseBrowserComputer).filter((computer): computer is BrowserComputer => computer !== null)
    const activeNodeId = nonEmptyString(record.activeNodeId) ?? undefined
    return { version: 2, computers, ...(activeNodeId ? { activeNodeId } : {}) }
}

function browserSummary(computer: BrowserComputer): ComputerSummary {
    return {
        kind: "remote",
        name: computer.name,
        nodeId: computer.nodeId,
        routes: computer.routes,
        access: accessForRoutes(computer.routes),
        addedAt: computer.addedAt,
    }
}

function browserConnection(computer: BrowserComputer): ComputerConnection {
    return { ...browserSummary(computer), token: computer.token }
}

class BrowserComputerBackend implements ComputerBackend {
    async list(): Promise<ComputerListState> {
        const state = this.read()
        const activeNodeId =
            state.activeNodeId && state.computers.some((computer) => computer.nodeId === state.activeNodeId)
                ? state.activeNodeId
                : (state.computers[0]?.nodeId ?? "")
        return { activeNodeId, computers: state.computers.map(browserSummary) }
    }

    async getConnection(nodeId?: string): Promise<ComputerConnection> {
        const state = this.read()
        const selectedNodeId = nodeId ?? state.activeNodeId ?? state.computers[0]?.nodeId
        const connection = state.computers.find((computer) => computer.nodeId === selectedNodeId)
        if (!connection) throw new Error("Pair this browser with a computer to continue")
        return browserConnection(connection)
    }

    async refreshAccess(_nodeId: string): Promise<ComputerListState> {
        return this.list()
    }

    async add(input: AddRemoteComputerInput): Promise<ComputerListState> {
        const name = nonEmptyString(input.name)
        const nodeId = nonEmptyString(input.nodeId)
        const token = nonEmptyString(input.token)
        const routes = normalizeNestRoutes(input.routes)
        if (!name || !nodeId || !token) throw new Error("Computer name, node ID, routes, and pairing token are required")
        const state = this.read()
        const existing = state.computers.find((computer) => computer.nodeId === nodeId)
        const connection: BrowserComputer = {
            addedAt: existing?.addedAt ?? new Date().toISOString(),
            name,
            nodeId,
            routes,
            token,
        }
        state.computers = [...state.computers.filter((computer) => computer.nodeId !== nodeId), connection]
        state.activeNodeId ??= nodeId
        this.write(state)
        return this.list()
    }

    async activate(nodeId: string): Promise<ComputerConnection> {
        const state = this.read()
        const connection = state.computers.find((computer) => computer.nodeId === nodeId)
        if (!connection) throw new Error(`Computer ${nodeId} is not registered`)
        state.activeNodeId = nodeId
        this.write(state)
        return browserConnection(connection)
    }

    async remove(nodeId: string): Promise<ComputerListState> {
        const state = this.read()
        state.computers = state.computers.filter((computer) => computer.nodeId !== nodeId)
        if (state.activeNodeId === nodeId) state.activeNodeId = state.computers[0]?.nodeId
        this.write(state)
        return this.list()
    }

    private read(): BrowserRegistryState {
        try {
            const value = window.localStorage.getItem(BROWSER_REGISTRY_KEY)
            return value ? parseBrowserRegistry(JSON.parse(value) as unknown) : emptyBrowserRegistry()
        } catch {
            return emptyBrowserRegistry()
        }
    }

    private write(state: BrowserRegistryState): void {
        window.localStorage.setItem(BROWSER_REGISTRY_KEY, JSON.stringify(state))
    }
}

function secureDescriptorSummary(descriptor: SecureComputerDescriptor): ComputerSummary {
    const routes = normalizeNestRoutes(descriptor.routes)
    return {
        kind: "remote",
        name: descriptor.name,
        nodeId: descriptor.nodeId,
        routes,
        access: accessForRoutes(routes),
        ...(descriptor.addedAt ? { addedAt: descriptor.addedAt } : {}),
    }
}

function secureConnection(connection: SecureComputerConnection): ComputerConnection {
    return { ...secureDescriptorSummary(connection), token: connection.token }
}

function secureListState(state: SecureRegistryState): ComputerListState {
    const computers = state.computers.map(secureDescriptorSummary)
    const activeNodeId = computers.some((computer) => computer.nodeId === state.activeNodeId) ? state.activeNodeId : (computers[0]?.nodeId ?? "")
    return { activeNodeId, computers }
}

class MobileComputerBackend implements ComputerBackend {
    async list(): Promise<ComputerListState> {
        return secureListState(await secureComputerRegistry.list())
    }

    async getConnection(nodeId?: string): Promise<ComputerConnection> {
        return secureConnection(await secureComputerRegistry.getConnection(nodeId))
    }

    async refreshAccess(_nodeId: string): Promise<ComputerListState> {
        return this.list()
    }

    async add(input: AddRemoteComputerInput): Promise<ComputerListState> {
        const name = nonEmptyString(input.name)
        const nodeId = nonEmptyString(input.nodeId)
        const token = nonEmptyString(input.token)
        const routes = normalizeNestRoutes(input.routes)
        if (!name || !nodeId || !token) throw new Error("Computer name, node ID, routes, and pairing token are required")
        return secureListState(await secureComputerRegistry.add({ name, nodeId, token, routes }))
    }

    async activate(nodeId: string): Promise<ComputerConnection> {
        return secureConnection(await secureComputerRegistry.activate(nodeId))
    }

    async remove(nodeId: string): Promise<ComputerListState> {
        return secureListState(await secureComputerRegistry.remove(nodeId))
    }
}

class ElectronComputerBackend implements ComputerBackend {
    private get api() {
        const api = window.wildlarkAPI?.nest.computers
        if (!api) throw new Error("Electron computer registry is unavailable")
        return api
    }

    list(): Promise<ComputerListState> {
        return this.api.list()
    }

    refreshAccess(nodeId: string): Promise<ComputerListState> {
        return this.api.refreshAccess(nodeId)
    }

    getConnection(nodeId?: string): Promise<ComputerConnection> {
        return this.api.getConnection(nodeId)
    }

    add(input: AddRemoteComputerInput): Promise<ComputerListState> {
        return this.api.add(input)
    }

    activate(nodeId: string): Promise<ComputerConnection> {
        return this.api.activate(nodeId)
    }

    remove(nodeId: string): Promise<ComputerListState> {
        return this.api.remove(nodeId)
    }
}

export function createComputerBackend(): ComputerBackend {
    if (window.wildlarkAPI?.nest.computers) return new ElectronComputerBackend()
    if (isNativeMobilePlatform()) return new MobileComputerBackend()
    return new BrowserComputerBackend()
}
